import { useEffect, useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHero from "@/components/base/PageHero";
import WorkCard from "@/components/base/WorkCard";
import {
  filterWorks,
  type WorkFilter,
  workGenres,
  workList,
  workRegions,
  workTypes,
  workYears,
} from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_STEP = 8;

interface SelectConfig {
  key: keyof WorkFilter;
  label: string;
  options: string[];
}

const selects: SelectConfig[] = [
  { key: "type", label: "作品类型", options: workTypes },
  { key: "genre", label: "题材", options: workGenres },
  { key: "region", label: "地区", options: workRegions },
  { key: "year", label: "年份", options: workYears },
];

const defaultFilter: WorkFilter = { type: "全部", genre: "全部", region: "全部", year: "全部" };

export default function RecommendationsPage() {
  usePageMeta(
    "动漫推荐 - 78动漫",
    "78动漫动漫作品推荐栏目，精选热门与经典动画作品，支持按类型、题材、地区与年份组合筛选，发现你想看的下一个番。",
    "动漫推荐,动画作品,TV动画,剧场版,作品筛选,78动漫",
  );

  const [filter, setFilter] = useState<WorkFilter>(defaultFilter);
  const [visible, setVisible] = useState(PAGE_STEP);
  const [filterOpen, setFilterOpen] = useState(false);

  const results = useMemo(() => filterWorks(workList, filter), [filter]);
  const shown = results.slice(0, visible);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [filter]);

  const updateFilter = (key: keyof WorkFilter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  const activeCount = selects.filter((config) => filter[config.key] && filter[config.key] !== "全部").length;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHero
          icon="ri-movie-line"
          title="动漫推荐"
          subtitle="热门及经典动漫推荐合集，按作品类型、题材、地区与年份自由组合筛选，快速定位你想看的作品。"
          breadcrumb={[{ label: "动漫推荐" }]}
        />

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground-700">
                <i className="ri-filter-3-line text-lg text-primary-600" />
                筛选条件
                {activeCount > 0 && (
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
                    已选 {activeCount}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilter(defaultFilter)}
                    className="flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-refresh-line" />
                    重置
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setFilterOpen((prev) => !prev)}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 md:hidden cursor-pointer"
                >
                  {filterOpen ? "收起" : "展开"}
                  <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </button>
              </div>
            </div>

            <div
              className={`${filterOpen ? "grid" : "hidden"} mt-4 grid-cols-1 gap-4 sm:grid-cols-2 md:mt-4 md:grid lg:grid-cols-4`}
            >
              {selects.map((config) => (
                <label key={config.key} className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground-500">{config.label}</span>
                  <div className="relative">
                    <select
                      value={filter[config.key] || "全部"}
                      onChange={(event) => updateFilter(config.key, event.target.value)}
                      className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-900 transition-colors focus:border-primary-400 focus:outline-none cursor-pointer"
                    >
                      {config.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
                  </div>
                </label>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="font-semibold text-foreground-900">{results.length}</strong> 部作品，当前显示{" "}
            {shown.length} 部
          </p>

          {shown.length > 0 ? (
            <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {shown.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-emotion-sad-line text-2xl" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">没有符合当前条件组合的作品，试试调整筛选条件。</p>
              <button
                type="button"
                onClick={() => setFilter(defaultFilter)}
                className="mt-5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}

          {visible < results.length && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_STEP)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
              >
                <i className="ri-add-line text-lg" />
                加载更多作品
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}